import { OverflowContainer } from "overflwn";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Flex, Text, Box } from "@similarweb/ui-components/foundations";

import { COMPANY_ANALYSIS_SIGNALS } from "app/pages/sales-intelligence/constants/routes";
import { iconNamePerSignalType } from "app/pages/sales-intelligence/modules/company-analysis/constants/signalsIcons";
import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import { useCompanyBaseInfoQuery } from "app/pages/sales-intelligence/modules/company-analysis/queries/useCompanyBaseInfoQuery";
import signalEventsService from "app/pages/sales-intelligence/modules/company-analysis/services/signals/signalEventsService";
import type { SignalEventDto } from "app/pages/sales-intelligence/modules/company-analysis/types/signals";
import SignalEventBoxItemFooter from "app/pages/sales-intelligence/pages/company-analysis/components/cards/signals-card/SignalEventBoxFooter/SignalEventBoxFooter";
import SignalEventInfoBox from "app/pages/sales-intelligence/pages/company-analysis/components/cards/signals-card/SignalEventInfoBox/SignalEventInfoBox";
import TruncatedTextWithTooltip from "app/pages/sales-intelligence/pages/company-analysis/components/shared/TruncatedTextWithTooltip/TruncatedTextWithTooltip";
import { translate } from "app/filters/i18nFilter";
import SignalType from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalType";
import type { SignalsDescription } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalsDescription";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { StyledSignalBoxTag } from "./styles";

type SignalEventBoxItemProps = {
    signal: SignalEventDto;
    signalsDescription: Readonly<SignalsDescription> | null;
    onArticleLinkClick: (signalEvent: SignalEventDto) => void;
    onCompanyLinkClick: (signalEvent: SignalEventDto) => void;
};

const SignalEventBoxItem = ({
    signal,
    signalsDescription,
    onArticleLinkClick,
    onCompanyLinkClick,
}: SignalEventBoxItemProps) => {
    const { params } = useCurrentStateAndParamsWithFixedDomain();

    const { data: companyInfo } = useCompanyBaseInfoQuery(params.key);
    const { type } = signal;

    const title = signalEventsService.getSignalEventTitle(signal, companyInfo);
    const country = signalEventsService.getSignalEventCountry(signal, true);

    const tags = signalEventsService.getSignalEventTags(signal, { omitTypesRenderedInInfoBox: true });

    const handleAllSignalsClick = () => void proNavigator.go(COMPANY_ANALYSIS_SIGNALS);

    return (
        <Flex
            data-automation="signal-event-item"
            data-automation-type={type}
            flexGrow={1}
            flexDirection="column"
            justifyContent="space-between"
            padding="12px 16px"
            borderRadius="6px"
            background={colorsPalettes.bluegrey["100"]}
            width="calc(50% - 8px)"
            maxWidth="calc(50% - 8px)"
            minHeight="168px"
        >
            <Flex
                gap="4px"
                flexDirection="column"
            >
                <Flex gap="8px">
                    <SWReactIcons
                        iconName={iconNamePerSignalType[type]}
                        size="xs"
                        iconColor={colorsPalettes.blue["400"]}
                        data-automation="signal-event-icon"
                    />
                    <Text
                        data-automation="signal-event-subtype"
                        fontVariant="caption"
                        color={colorsPalettes.carbon[400]}
                    >
                        {translate(`si.signals.type.${type}.text`)}
                    </Text>
                </Flex>
                <TruncatedTextWithTooltip
                    data-automation="signal-event-title"
                    height={40}
                    text={title}
                    textStyles={{
                        fontVariant: "body1",
                        color: colorsPalettes.carbon[500],
                    }}
                    country={country?.id}
                />
                <SignalEventInfoBox
                    signal={signal}
                    companyInfo={companyInfo}
                    signalsDescription={signalsDescription}
                    onCompanyLinkClick={onCompanyLinkClick}
                />
                <Box margin="4px 0 0">
                    <OverflowContainer
                        renderHiddenElements={(hidden) => (
                            <Tooltip content={tags.slice(tags.length - hidden.length).toString()}>
                                <span onClick={handleAllSignalsClick}>+{hidden.length} more</span>
                            </Tooltip>
                        )}
                        gap={4}
                    >
                        {tags.map((tag) => (
                            <StyledSignalBoxTag key={tag}>{tag}</StyledSignalBoxTag>
                        ))}
                    </OverflowContainer>
                </Box>
            </Flex>
            <SignalEventBoxItemFooter
                signal={signal}
                onArticleLinkClick={onArticleLinkClick}
            />
        </Flex>
    );
};

export default SignalEventBoxItem;
